/**
 * TRUS-762 per-org rollout: the `member_profile_v2` capability.
 *
 * An org that holds it sees the TRUS-762 member profile (one Activities view,
 * one Programs list, Member setup gear, one Engagement tab, member forms hub,
 * pinned forms, the restyled forms library and form editor). An org without it
 * keeps the previous screens, restored verbatim as *Legacy components. Platform
 * admins switch it per organization.
 *
 * While the capability is loading nothing is rendered, so an org on the
 * previous profile never sees a flash of the new one.
 */
import React from 'react';
import { useCapability } from '@/hooks/useCapabilities';

export const MEMBER_PROFILE_V2 = 'member_profile_v2';

const activeCboId = (): number | null => {
  try {
    return parseInt(localStorage.getItem('active_cbo_id') || '0', 10) || null;
  } catch {
    return null;
  }
};

/** Whether the active org runs the TRUS-762 member profile. */
export function useMemberProfileV2(): { enabled: boolean; loading: boolean } {
  const { enabled, loading } = useCapability(activeCboId(), MEMBER_PROFILE_V2);
  return { enabled, loading };
}

/**
 * Render `V2` for an org on the new profile and `Legacy` otherwise; nothing
 * until the capability is known.
 */
export function withMemberProfileV2<P extends object>(
  V2: React.ComponentType<any>,
  Legacy: React.ComponentType<any>,
  displayName: string,
): React.FC<P> {
  const Switch: React.FC<P> = (props) => {
    const { enabled, loading } = useMemberProfileV2();
    if (loading) return null;
    return enabled ? <V2 {...props} /> : <Legacy {...props} />;
  };
  Switch.displayName = displayName;
  return Switch;
}
